/**
 * Intercom live chat for the hosted storefront. boxed parity (`components/Intercom.tsx` there):
 * one Messenger per page, booted with the shopper's identity when there is one, shut down and
 * booted again when that identity changes. Loaded only when the tenant's storefront has chat on:
 * the SDK is imported lazily and its widget script is fetched only once `syncIntercom` runs with
 * an app id, so a store without chat ships and loads nothing from Intercom. Never throws and
 * never blocks the shopper — a Messenger that fails to load is Intercom's problem, not theirs.
 */
import type { StorefrontIntercom } from "@loot-os/api-contract";

import type { TenantConfig } from "./tenant.js";

type Sdk = typeof import("@intercom/messenger-js-sdk");

/** The signed-in shopper as the Messenger identifies them; minted by the platform, never here. */
export interface IntercomIdentity {
  userId: string;
  userHash: string;
  email: string;
}

export interface IntercomTarget {
  appId: string;
  /** The tenant, as an Intercom company: how agents tell one store's shoppers from another's. */
  company: { id: string; name: string };
  attributes: { storefront_hostname: string; storefront_mode: "live" | "test" };
  /** The signed-in shopper, or null for an anonymous visitor. */
  identity: IntercomIdentity | null;
}

export interface IntercomState {
  unread: number;
  open: boolean;
}

const OFF: StorefrontIntercom = { appId: "", launcherIconSvg: "" };

/** Fail-closed read: a config cached by an older BFF or in sessionStorage has no `intercom`. */
export function intercomOf(config: TenantConfig): StorefrontIntercom {
  return (config as Partial<TenantConfig>).intercom ?? OFF;
}

let sdkPromise: Promise<Sdk | null> | null = null;

function loadSdk(): Promise<Sdk | null> {
  sdkPromise ??= import("@intercom/messenger-js-sdk").then(
    (mod) => mod,
    () => null,
  );
  return sdkPromise;
}

/** What the Messenger was last brought to; null = shut down or never booted. */
let current: { appId: string; userId: string | null } | null = null;
/** A Messenger session exists: `Intercom()`/`boot()` ran without a `shutdown()` since. */
let booted = false;
let state: IntercomState = { unread: 0, open: false };
const listeners = new Set<(state: IntercomState) => void>();
/** SDK calls run in order behind the lazy import, so a click can't overtake the boot. */
let chain: Promise<void> = Promise.resolve();

function run(step: (sdk: Sdk) => void): void {
  chain = chain.then(async () => {
    const sdk = await loadSdk();
    if (!sdk) return;
    try {
      step(sdk);
    } catch {
      // Intercom's problem, never the shopper's.
    }
  });
}

function setState(patch: Partial<IntercomState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener(state);
}

function settingsFor(target: IntercomTarget) {
  return {
    app_id: target.appId,
    // The storefront draws its own launcher (IntercomLauncher); the Messenger window opens above
    // it, in the same corner.
    hide_default_launcher: true,
    alignment: "right",
    horizontal_padding: 16,
    vertical_padding: 76,
    company: { company_id: target.company.id, name: target.company.name },
    ...target.attributes,
    ...(target.identity
      ? {
          user_id: target.identity.userId,
          user_hash: target.identity.userHash,
          email: target.identity.email,
        }
      : {}),
  };
}

/**
 * Re-registered after every boot: `shutdown()` may drop them, and a duplicate registration is
 * harmless because every callback only replaces state.
 */
function installCallbacks(sdk: Sdk): void {
  sdk.onShow(() => setState({ open: true }));
  sdk.onHide(() => setState({ open: false }));
  sdk.onUnreadCountChange((count: unknown) =>
    setState({ unread: typeof count === "number" ? count : 0 }),
  );
}

function sameTarget(a: typeof current, b: typeof current): boolean {
  if (a === null || b === null) return a === b;
  return a.appId === b.appId && a.userId === b.userId;
}

/**
 * Brings the Messenger to `target` (null = off). Idempotent on the app id and shopper, so
 * StrictMode's doubled effect and unrelated re-renders are no-ops; an identity change ends the
 * session and starts a new one, which also drops Intercom's visitor cookie so the next person
 * on this browser is not the previous shopper.
 */
export function syncIntercom(target: IntercomTarget | null): void {
  const next = target ? { appId: target.appId, userId: target.identity?.userId ?? null } : null;
  if (sameTarget(current, next)) return;
  current = next;
  run((sdk) => {
    if (!target) {
      if (booted) sdk.shutdown();
      booted = false;
      setState({ unread: 0, open: false });
      return;
    }
    const settings = settingsFor(target);
    if (booted) {
      // A second `Intercom()` call only issues `update`, which never switches users.
      sdk.shutdown();
      sdk.boot(settings);
    } else {
      // Records the settings and injects the widget script, which boots from them.
      sdk.Intercom({ ...settings, region: "us" });
    }
    booted = true;
    installCallbacks(sdk);
  });
}

/** The launcher's click: open the Messenger, or close it if it is open. */
export function toggleIntercom(): void {
  if (!current) return;
  run((sdk) => {
    if (!booted) return;
    if (state.open) sdk.hide();
    else sdk.show();
  });
}

/** Per SPA navigation: an empty `update` makes Intercom check for new messages. */
export function noteRouteChange(): void {
  if (!current) return;
  run((sdk) => {
    if (booted) sdk.update({});
  });
}

/** Subscribes to unread count and open state; calls back at once with the current values. */
export function onIntercomState(listener: (state: IntercomState) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}
